import React, { createContext, useContext, useState } from 'react';

const PrinterContext = createContext({ 
  selectedDevice: null, 
  printerConnection: { loading: false, success: false, failure: false },
  updatePrinterConnection: (newPrinterConnection: { loading: boolean, success: boolean, failure: boolean }) => {},
  saveSelectedPrinter: (device: any) => {},
  printBarcode: (barcodeData: Array<string>, text_1: string, text_2: string, text_3: string, qty_to_print: number) => {} ,
  printShippingLabel: (zpl: string) => {}
});

export const usePrinter = () => {
  return useContext(PrinterContext);
};

export const PrinterProvider = ({ children }) => {
  const [selectedDevice, setSelectedDevice] = useState(null as any);
  const [printerConnection, setPrinterConnection] = useState({ loading: false, success: false, failure: false });

  // Function to update the printer connection state
  const updatePrinterConnection = (status) => {
    setPrinterConnection(status);
  };

  const saveSelectedPrinter = (device) => {
    setSelectedDevice(device);
    localStorage.setItem('barcode_printer', JSON.stringify(device));
  };
  
  function generateZpl(barcodeData, manufacturer, partNumber, partName, include_date) {
    const labelWidth = 406; // 2 inches in dots
    const labelHeight = 203; // 1 inch in dots
    const barcodeHeight = 50; // Barcode height

    // Dynamically adjust barcode width based on length
    const moduleWidth = barcodeData.length <= 12 ? 2 : 1;

    // Improved barcode width calculation (scales correctly)
    const barcodeWidth = Math.min(((barcodeData.length * 11) + 35) * moduleWidth, labelWidth - 20);
    
    // Correct centering formula
    const barcodeX = Math.max((labelWidth - barcodeWidth) / 2, 0);

    const fieldWidth = labelWidth - 40; // Leaves margins
    const today = new Date().toLocaleDateString('en-US');

    // Dynamically adjust part number font size
    let partNumberFontSize = "40,40"; // Default
    let partNameHeight = 85;
    if (partNumber.length <= 10) {
      partNumberFontSize = "50,50"; 
    }

    if (partNumber.length <= 7) {
      partNumberFontSize = "80,80"; 
      partNameHeight = 110; 
    }

    if (partNumber.length <= 5) {
      partNumberFontSize = "100,100";
      partNameHeight = 120;
    }

    if (partNumber.length > 16) {
      partNumberFontSize = "30,30"; 
    }

    const date_string = include_date ? `
        ^FO20,${labelHeight - 15}^A0N,16,16^CI28^FB${fieldWidth},1,0,C  // Date now at the bottom
        ^FD${today}^FS` : ``;

    return `
        ^XA
        ^PW${labelWidth}
        ^LL${labelHeight}

        ^FO${barcodeX},10^BY${moduleWidth},2.0,${barcodeHeight}  
        ^BCN,${barcodeHeight},N,N,N
        ^FD${barcodeData}^FS

        ^FO20,${barcodeHeight + 15}^A0N,20,20^CI28^FB${fieldWidth},1,0,C
        ^FD${manufacturer}^FS

        ^FO20,${barcodeHeight + 40}^A0N,${partNumberFontSize}^CI28^FB${fieldWidth},1,0,C  
        ^FD${partNumber}^FS

        ^FO20,${barcodeHeight + partNameHeight}^A0N,20,20^CI28^FB${fieldWidth},2,0,C  // Allow up to 2 lines for long names
        ^FD${partName}^FS

        ${date_string}

        ^XZ
    `;
};

  // Function to print a barcode
  // Text 1, 2, 3 is the order in which the text will appear on the label starting from the top
  const printBarcode = (barcode_data: Array<string>, text_1: string, text_2: string, text_3: string, qty_to_print: number, include_date = true) => {
    if (!selectedDevice) {
      handleNoSelectedDevice();
      return;
    }

    const barcode_data_string = prepBarcodeDataString(barcode_data);

    // const zpl = getZPL(printable_id, printable_number, printable_name, size);
    const zpl = generateZpl(barcode_data_string, text_1, text_2, text_3, include_date);
    
    for (let printed = 1; printed <= qty_to_print; printed++) {
      sendPrintCommandToPrinter(zpl);
    }
  };

  const printShippingLabel = (zpl: string) => {
      if (!selectedDevice) {
      handleNoSelectedDevice();
      return;
    }

    sendPrintCommandToPrinter(zpl);
  }

  function handleNoSelectedDevice() {
    console.error("No printer selected.");
    updatePrinterConnection({ loading: false, success: false, failure: true });
  }

  function prepBarcodeDataString(barcode_data: Array<string>) {
    let data_string = '';
  
    barcode_data.forEach((data, index) => {
      if ((index + 1) < barcode_data.length) {
        data_string += data + '|+';
      } else if ((index + 1) == barcode_data.length) {
        data_string += data;
      }
    });

    return data_string;
  }

  function sendPrintCommandToPrinter(zpl: string) {
    selectedDevice?.send(zpl, () => {
    }, (error) => {
      console.error("Print failed:", error);
      updatePrinterConnection({ loading: false, success: false, failure: true });
    });
  }

  return (
    <PrinterContext.Provider value={{
      selectedDevice,
      printerConnection,
      saveSelectedPrinter,
      updatePrinterConnection, // Provide this function in the context
      printBarcode,
      printShippingLabel
    }}>
      {children}
    </PrinterContext.Provider>
  );
};
